import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Ticket } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useCreateVoucherMutation } from '@/client/api/voucher/voucher.mutation';
import { useVoucherTypesQuery } from '@/client/api/voucher/voucher.query';
import { CreateVoucherPayload, getApiErrorCode, VoucherScopeInput, VoucherType } from '@/client/types/voucher';
import VoucherScopesEditor from './VoucherScopesEditor';

interface CreateVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const emptyForm = {
  vtid: '',
  code: '',
  name: '',
  description: '',
  ccode: 'GHS',
  value: '',
  max_discount_amount: '',
  min_purchase_amount: '',
  max_redemptions: '',
  max_redemptions_per_user: '',
  date_valid_from: '',
  date_valid_until: '',
  oid_issuer: '',
  storeid: '',
  uid_assigned: '',
};

type FormState = typeof emptyForm;

const parseOptionalNumber = (raw: string): number | undefined => {
  if (raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

const toIsoOrUndefined = (raw: string): string | undefined => {
  if (!raw.trim()) return undefined;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
};

const CreateVoucherModal: React.FC<CreateVoucherModalProps> = ({ isOpen, onClose }) => {
  const [form, setForm] = useState<FormState>(emptyForm);
  const [scopes, setScopes] = useState<VoucherScopeInput[]>([]);
  const { data: types = [], isLoading: typesLoading } = useVoucherTypesQuery();
  const { mutateAsync, isPending } = useCreateVoucherMutation();

  const selectedType: VoucherType | undefined = useMemo(
    () => types.find((t) => String(t.vtid) === form.vtid),
    [types, form.vtid],
  );
  const valueType = (selectedType?.value_type || 'FIXED').toUpperCase();

  useEffect(() => {
    if (!isOpen) return;
    setForm(emptyForm);
    setScopes([]);
  }, [isOpen]);

  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleClose = () => {
    if (isPending) return;
    setForm(emptyForm);
    setScopes([]);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.vtid) {
      toast.error('Select a voucher type');
      return;
    }

    const vtid = Number(form.vtid);
    let value = 0;
    if (valueType === 'NONE') {
      value = 0;
    } else {
      const parsed = Number(form.value);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        toast.error(valueType === 'PERCENT' ? 'Enter a percent between 0 and 100' : 'Enter a positive value');
        return;
      }
      if (valueType === 'PERCENT' && parsed > 100) {
        toast.error('Percent must be 0–100');
        return;
      }
      value = parsed;
    }

    const payload: CreateVoucherPayload = {
      vtid,
      value,
      ccode: form.ccode.trim() || 'GHS',
    };

    if (form.code.trim()) payload.code = form.code.trim().toUpperCase();
    if (form.name.trim()) payload.name = form.name.trim();
    if (form.description.trim()) payload.description = form.description.trim();

    const from = toIsoOrUndefined(form.date_valid_from);
    const until = toIsoOrUndefined(form.date_valid_until);
    if (from) payload.date_valid_from = from;
    if (until) payload.date_valid_until = until;
    if (from && until && new Date(until) <= new Date(from)) {
      toast.error('End date must be after start date');
      return;
    }

    const minPurchase = parseOptionalNumber(form.min_purchase_amount);
    if (minPurchase != null) payload.min_purchase_amount = minPurchase;

    if (valueType === 'PERCENT') {
      const cap = parseOptionalNumber(form.max_discount_amount);
      if (cap != null) payload.max_discount_amount = cap;
    }

    const maxRedemptions = parseOptionalNumber(form.max_redemptions);
    if (maxRedemptions != null) payload.max_redemptions = maxRedemptions;
    const maxPerUser = parseOptionalNumber(form.max_redemptions_per_user);
    if (maxPerUser != null) payload.max_redemptions_per_user = maxPerUser;

    const oid = parseOptionalNumber(form.oid_issuer);
    if (oid != null) payload.oid_issuer = oid;
    const storeid = parseOptionalNumber(form.storeid);
    if (storeid != null) payload.storeid = storeid;
    const uid = parseOptionalNumber(form.uid_assigned);
    if (uid != null) payload.uid_assigned = uid;
    if (scopes.length > 0) payload.scopes = scopes;

    try {
      const created = await mutateAsync(payload);
      toast.success(`Voucher "${created.code}" created`, { richColors: true });
      handleClose();
    } catch (error: any) {
      const code = getApiErrorCode(error);
      if (code === 'code_exists') {
        toast.error('That code is already taken — choose another', { richColors: true });
      } else if (code === 'invalid_percentage') {
        toast.error('Percent value must be between 0 and 100', { richColors: true });
      } else if (code === 'invalid_scope') {
        toast.error('One or more scopes are invalid — check type, ref id, and mode', {
          richColors: true,
        });
      } else {
        toast.error(error?.message || 'Failed to create voucher', { richColors: true });
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ticket className="h-5 w-5 text-blue-600" />
            Create voucher
          </DialogTitle>
          <DialogDescription>
            Creates a payment-integrated voucher. Leave code blank to auto-generate.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="vtid">Type *</Label>
            <select
              id="vtid"
              required
              value={form.vtid}
              onChange={(e) => setField('vtid', e.target.value)}
              disabled={typesLoading || isPending}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">{typesLoading ? 'Loading types…' : 'Select type'}</option>
              {types.map((t) => (
                <option key={t.vtid} value={t.vtid}>
                  {t.name} ({t.name_system})
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="code">Code</Label>
              <Input
                id="code"
                placeholder="Optional — auto if blank"
                value={form.code}
                onChange={(e) => setField('code', e.target.value.toUpperCase())}
                disabled={isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ccode">Currency</Label>
              <Input
                id="ccode"
                value={form.ccode}
                onChange={(e) => setField('ccode', e.target.value.toUpperCase())}
                disabled={isPending}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              disabled={isPending}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              disabled={isPending}
            />
          </div>

          {valueType !== 'NONE' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="value">
                  {valueType === 'PERCENT' ? 'Percent off *' : 'Value *'}
                </Label>
                <Input
                  id="value"
                  type="number"
                  min={valueType === 'PERCENT' ? 0 : 0.01}
                  max={valueType === 'PERCENT' ? 100 : undefined}
                  step="any"
                  required
                  value={form.value}
                  onChange={(e) => setField('value', e.target.value)}
                  disabled={isPending}
                />
              </div>
              {valueType === 'PERCENT' && (
                <div className="space-y-2">
                  <Label htmlFor="max_discount_amount">Max discount amount</Label>
                  <Input
                    id="max_discount_amount"
                    type="number"
                    min={0}
                    step="any"
                    value={form.max_discount_amount}
                    onChange={(e) => setField('max_discount_amount', e.target.value)}
                    disabled={isPending}
                  />
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="date_valid_from">Valid from</Label>
              <Input
                id="date_valid_from"
                type="datetime-local"
                value={form.date_valid_from}
                onChange={(e) => setField('date_valid_from', e.target.value)}
                disabled={isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="date_valid_until">Valid until</Label>
              <Input
                id="date_valid_until"
                type="datetime-local"
                value={form.date_valid_until}
                onChange={(e) => setField('date_valid_until', e.target.value)}
                disabled={isPending}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="min_purchase_amount">Min purchase</Label>
              <Input
                id="min_purchase_amount"
                type="number"
                min={0}
                step="any"
                value={form.min_purchase_amount}
                onChange={(e) => setField('min_purchase_amount', e.target.value)}
                disabled={isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_redemptions">Max redemptions</Label>
              <Input
                id="max_redemptions"
                type="number"
                min={1}
                placeholder="Blank = single-use"
                value={form.max_redemptions}
                onChange={(e) => setField('max_redemptions', e.target.value)}
                disabled={isPending}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max_redemptions_per_user">Max per user</Label>
              <Input
                id="max_redemptions_per_user"
                type="number"
                min={1}
                value={form.max_redemptions_per_user}
                onChange={(e) => setField('max_redemptions_per_user', e.target.value)}
                disabled={isPending}
              />
            </div>
          </div>

          <div className="rounded-lg border border-gray-100 bg-gray-50 p-3 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Issuer / assignment</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="oid_issuer">Org ID</Label>
                <Input
                  id="oid_issuer"
                  type="number"
                  value={form.oid_issuer}
                  onChange={(e) => setField('oid_issuer', e.target.value)}
                  disabled={isPending}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="storeid">Store ID</Label>
                <Input
                  id="storeid"
                  type="number"
                  value={form.storeid}
                  onChange={(e) => setField('storeid', e.target.value)}
                  disabled={isPending}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="uid_assigned">Assigned user ID</Label>
                <Input
                  id="uid_assigned"
                  type="number"
                  value={form.uid_assigned}
                  onChange={(e) => setField('uid_assigned', e.target.value)}
                  disabled={isPending}
                />
              </div>
            </div>
          </div>

          <VoucherScopesEditor value={scopes} onChange={setScopes} disabled={isPending} />

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || typesLoading}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateVoucherModal;
